/**
 * Готовая сцена: посмотреть и выбрать вариант (§21, §24).
 *
 * Просьба клиента после первого теста: «по клику на видео не видно
 * результат». Так и было — карточка показывала кадр-обложку, панель
 * открывала редактор, а посмотреть, что получилось, было негде.
 *
 * Варианты копятся сами: каждая пересборка сцены создаёт новый, и каждый
 * стоил единиц. Показываем их полосой, чтобы можно было сравнить глазами
 * и вернуться к удачному.
 */
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { SceneView } from '@alterium/shared';
import { projectsApi } from '../../api/projects';

interface SceneResultProps {
  scene: SceneView;
  onChanged: () => void;
}

export function SceneResult({ scene, onChanged }: SceneResultProps) {
  const { t } = useTranslation();

  const select = useMutation({
    mutationFn: (variantId: string) => projectsApi.selectVariant(scene.id, variantId),
    onSuccess: onChanged,
  });

  if (!scene.videoUrl) return null;

  return (
    <section className="flex flex-col gap-3">
      <p className="text-meta text-content-muted">{t('scene.result')}</p>

      {/* key по ссылке: при смене варианта плеер должен перезагрузиться,
          иначе он продолжает показывать прежний файл. */}
      <video
        key={scene.videoUrl}
        src={scene.videoUrl}
        poster={scene.previewUrl ?? undefined}
        controls
        playsInline
        preload="metadata"
        className="w-full rounded-card bg-surface-secondary"
      />

      {scene.variants.length > 1 ? (
        <div className="flex flex-col gap-2">
          <p className="text-meta text-content-muted">
            {t('scene.variants', { count: scene.variants.length })}
          </p>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {scene.variants.map((variant, index) => (
              <button
                key={variant.id}
                type="button"
                onClick={() => select.mutate(variant.id)}
                disabled={select.isPending || variant.isSelected}
                aria-label={t('scene.variantNumber', { value: index + 1 })}
                className={
                  variant.isSelected
                    ? 'relative h-20 w-14 shrink-0 overflow-hidden rounded-control border-2 border-accent'
                    : 'relative h-20 w-14 shrink-0 overflow-hidden rounded-control border border-line transition-colors duration-fast hover:border-line-strong'
                }
              >
                {variant.posterUrl ? (
                  <img
                    src={variant.posterUrl}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="flex h-full items-center justify-center text-[11px] text-content-muted">
                    {index + 1}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}
